// WHAT A STATUS VALUE MEANS, as opposed to what it says (TGCLOUD-1784).
//
// Six places in this product branch on the wording of a clinical status. The
// home Overdue panel decides whether to chase an appointment, two overlay files
// decide what to draw, the pathway board picks a chip, and the referral form
// decides whether to reveal its closure fields. Every one of them compared
// English string literals, which meant a site could not rename "Closed" to
// "Seen in clinic" without silently breaking all six, and a value arriving from
// a Desktop migration or an import meant nothing to any of them.
//
// So a row now carries a MEANING and code reads that instead. Many rows may
// share one meaning, which is the point: a site can offer "Seen in clinic",
// "Discharged" and "Closed" and the Overdue panel knows all three mean the
// appointment is dealt with.
//
// THE FALLBACK IS NOT OPTIONAL. Every consumer here is synchronous and several
// run before, or entirely without, a fetch: the Overdue panel renders on the
// home screen, and the overlay evaluator is a pure function called per member
// per render. They cannot await a list. So this module keeps a frozen map of
// the values the product has always written, and rows learned from the server
// are layered on top of it. `statusSemantics.test.js` compares that frozen map
// against the server's own definitions, so the two cannot drift apart.
//
// A VALUE WITH NO MEANING IS NOT RESOLVED. That is the safe direction, and it
// is the behaviour this product already had: an appointment nobody has closed
// out is exactly what an outstanding-appointment overlay is looking for, and a
// pathway-created appointment carries no status at all.

// Which list backs which stored field. The overlay evaluator and the forms both
// speak in field names; everything else here speaks in list keys.
export const LIST_BY_FIELD = Object.freeze({
  appointmentStatus: 'appointmentStatuses',
  referralStatus: 'referralStatuses',
  contactStatus: 'contactStatuses',
  response: 'consentResponses'
});

// The values this product ships, and what each one means. Mirrors the seeds in
// backend/config/lookupDefinitions.js; the test asserts that, structurally,
// rather than by eye.
//
// THIS MAP PLAYS TWO ROLES, and TGCLOUD-1751 is what made them diverge:
//
//   MEANING   every shipped label resolves, INCLUDING the ones no longer
//             offered. Appointment Open and Closed are switched off, but the
//             records that hold them must still read as unresolved and
//             resolved respectively, before any list has loaded.
//   PICKER    only the labels a form should offer on first paint, which is
//             the seeds that ship switched ON (`offered: false` marks the
//             rest). `isDefault` marks what a NEW record starts on.
//
// The LABEL is kept as it is written, not normalised, because these strings are
// read back to a clinician: the overlay criteria popover prints the set it is
// about to compare against, and "status is none of sent / completed" is not
// what this product says.
const FALLBACK_MEANINGS = Object.freeze({
  appointmentStatuses: Object.freeze([
    { label: 'Booked', meaning: 'booked', isDefault: true },
    { label: 'Attended', meaning: 'attended' },
    { label: 'Did Not Attend', meaning: 'did_not_attend' },
    { label: 'Cancelled', meaning: 'cancelled' },
    { label: 'Moved', meaning: 'moved' },
    { label: 'Open', meaning: 'open', offered: false },
    { label: 'Closed', meaning: 'closed', offered: false }
  ]),
  referralStatuses: Object.freeze([
    { label: 'Open', meaning: 'open', isDefault: true },
    { label: 'Closed', meaning: 'closed' }
  ]),
  contactStatuses: Object.freeze([
    { label: 'Scheduled', meaning: 'scheduled', isDefault: true },
    { label: 'Sent / Completed', meaning: 'completed' },
    { label: 'Did Not Attend', meaning: 'did_not_attend' },
    { label: 'Cancelled', meaning: 'cancelled' }
  ]),
  consentResponses: Object.freeze([
    { label: 'Consent given', meaning: 'given' },
    { label: 'Denied consent', meaning: 'denied' },
    { label: 'Partial consent', meaning: 'partial' },
    { label: 'Withdrawn consent', meaning: 'withdrawn' },
    { label: 'No response', meaning: 'none' }
  ])
});

// Which meanings mean "dealt with, stop chasing it".
//
// The two status lists deliberately DISAGREE about a missed booking. A missed
// APPOINTMENT is resolved, matching how TrakGene Desktop derives its status: the
// appointment itself is over and a new one is booked as a new record. A missed
// CONTACT is not, because the contact is still owed and this product has an
// overlay built to chase exactly those. Making these two agree would look like
// tidying and would change who gets chased.
const RESOLVED_MEANINGS = Object.freeze({
  appointmentStatuses: Object.freeze([
    'attended',
    'did_not_attend',
    'cancelled',
    'moved',
    'closed'
  ]),
  referralStatuses: Object.freeze(['closed']),
  contactStatuses: Object.freeze(['completed', 'cancelled']),
  consentResponses: Object.freeze([])
});

// What a FORM needs to know about a meaning beyond whether it is resolved
// (TGCLOUD-1751), mirroring `meaningRules()` in lookupDefinitions.js; the test
// compares the two structurally.
//
//   legacy         kept only so records already holding it read correctly;
//                  never offered for anything new
//   availableFrom  'appointmentStart': not offered until the appointment's
//                  start has passed, because nobody can have attended, or
//                  failed to attend, an appointment that has not happened
//   takesReason    the form asks why (cancellation / move reasons)
//
// Keyed on the MEANING, so a site's own "Seen" row meaning `attended` inherits
// the rule without anyone configuring it.
const MEANING_RULES = Object.freeze({
  appointmentStatuses: Object.freeze({
    open: Object.freeze({ legacy: true }),
    attended: Object.freeze({ availableFrom: 'appointmentStart' }),
    did_not_attend: Object.freeze({ availableFrom: 'appointmentStart' }),
    cancelled: Object.freeze({ takesReason: true }),
    moved: Object.freeze({ takesReason: true }),
    closed: Object.freeze({ legacy: true })
  }),
  referralStatuses: Object.freeze({}),
  contactStatuses: Object.freeze({}),
  consentResponses: Object.freeze({})
});

const NO_RULE = Object.freeze({});

// Rows learned from the server, layered over the fallback. Module-level rather
// than context, because the consumers are pure functions and a hook cannot
// reach them.
const learned = new Map();

const norm = (value) =>
  typeof value === 'string'
    ? value.trim().toLowerCase().replace(/\s+/g, ' ')
    : '';

/**
 * Record what a group's rows mean. Called by `useLookupList` whenever it
 * fetches a list that carries meanings.
 *
 * A COMPLETE read (disabled and withdrawn rows included, which is what the
 * Overdue panel, the pathway board and the overlays ask for) REPLACES what was
 * known. A picker's read is only the rows it offers, so it is MERGED: it must
 * not make a switched-off row's wording forget its meaning, because records
 * still hold that wording (TGCLOUD-1751).
 *
 * @param {string} listKey
 * @param {Array<{label: string, meaning: string|null, formerLabels?: string[]}>} rows
 * @param {{ complete?: boolean }} [options]
 */
export const setStatusRows = (listKey, rows, { complete = true } = {}) => {
  if (!listKey || !Array.isArray(rows)) return;
  const map = complete ? new Map() : new Map(learned.get(listKey) || []);
  for (const row of rows) {
    if (!row) continue;
    if (!row.meaning) {
      // A row whose meaning has been cleared stops meaning anything.
      map.delete(norm(row.label));
      continue;
    }
    map.set(norm(row.label), { meaning: row.meaning, label: row.label });
    // A spelling the row used to be offered under still resolves, so a record
    // written before a relabel does not become meaningless. It is NOT offered
    // back as a label, though: it is not what the clinic calls it any more.
    for (const former of row.formerLabels || []) {
      if (!map.has(norm(former))) {
        map.set(norm(former), { meaning: row.meaning, label: null });
      }
    }
  }
  learned.set(listKey, map);
};

/** Forget everything learned. Exists for tests; nothing in the app calls it. */
export const resetStatusRows = () => learned.clear();

/**
 * What the product understands a stored value to be.
 *
 * @returns {string|null} the meaning, or null when nothing recognises it
 */
export const meaningOf = (listKey, storedValue) => {
  const key = norm(storedValue);
  if (!key) return null;

  const fromServer = learned.get(listKey);
  if (fromServer && fromServer.has(key)) return fromServer.get(key).meaning;

  const fallback = FALLBACK_MEANINGS[listKey] || [];
  const hit = fallback.find((entry) => norm(entry.label) === key);
  return hit ? hit.meaning : null;
};

/** Same, for a caller that only knows the field name on the record. */
export const meaningOfField = (field, storedValue) =>
  meaningOf(LIST_BY_FIELD[field], storedValue);

/**
 * Is this value one the product should stop chasing?
 *
 * A value nothing recognises is NOT resolved, which is what keeps an imported
 * or unrecognised status visible rather than quietly closing it.
 */
export const isResolved = (listKey, storedValue) => {
  const meaning = meaningOf(listKey, storedValue);
  if (!meaning) return false;
  return (RESOLVED_MEANINGS[listKey] || []).includes(meaning);
};

/**
 * Every wording currently known to carry one of these meanings.
 *
 * This is what lets the overlay presets keep comparing values, which is all
 * their evaluator can do, while the SET they compare against follows whatever
 * a site has configured. A site that renames its resolved appointment status
 * to "Seen in clinic" gets that wording in the set the moment its rows load.
 *
 * @param {string} listKey
 * @param {string[]} meanings
 * @returns {string[]} labels, in the product's own spelling
 */
export const labelsMeaning = (listKey, meanings) => {
  const wanted = new Set(meanings || []);
  const labels = new Map();

  for (const entry of FALLBACK_MEANINGS[listKey] || []) {
    if (wanted.has(entry.meaning)) labels.set(norm(entry.label), entry.label);
  }

  const fromServer = learned.get(listKey);
  if (fromServer) {
    for (const [key, entry] of fromServer.entries()) {
      // A former spelling carries no label: it still RESOLVES, but offering it
      // back would print a name the clinic has stopped using.
      if (wanted.has(entry.meaning) && entry.label) {
        labels.set(key, entry.label);
      }
    }
  }
  return [...labels.values()];
};

/** Every meaning this list treats as resolved. */
export const resolvedMeaningsFor = (listKey) =>
  RESOLVED_MEANINGS[listKey] || [];

/**
 * The wording a picker offers before any rows have loaded: the shipped values
 * that ship switched ON. Never a legacy value such as appointment Open or
 * Closed, which still RESOLVE through `meaningOf` but are not offered.
 */
export const fallbackLabelsFor = (listKey) =>
  (FALLBACK_MEANINGS[listKey] || [])
    .filter((entry) => entry.offered !== false)
    .map((entry) => entry.label);

/**
 * What a NEW record starts on when no rows have loaded, or none is marked the
 * default: the shipped default, which for appointments is Booked.
 */
export const fallbackDefaultLabel = (listKey) =>
  (FALLBACK_MEANINGS[listKey] || []).find((entry) => entry.isDefault)?.label ??
  null;

/**
 * The label a new record should start on: the configured default row if the
 * rows carry one, else the shipped default.
 *
 * @param {Array<{label: string, isDefault?: boolean}>} rows
 * @param {string} listKey
 */
export const defaultStatusLabel = (rows, listKey) =>
  (rows || []).find((row) => row && row.isDefault && row.label)?.label ??
  fallbackDefaultLabel(listKey);

/** The form rules for a meaning; an empty object when there are none. */
export const meaningRule = (listKey, meaning) =>
  (meaning && MEANING_RULES[listKey]?.[meaning]) || NO_RULE;

/** The form rules for a stored value, through what it means. */
export const ruleOfValue = (listKey, storedValue) =>
  meaningRule(listKey, meaningOf(listKey, storedValue));

/** Kept only so older records read correctly; never offered for new ones. */
export const isLegacyMeaning = (listKey, meaning) =>
  Boolean(meaningRule(listKey, meaning).legacy);

/**
 * The wording a status Select should offer.
 *
 * Falls back to what this product has always written when no rows have loaded,
 * so a form shows its usual options on first paint rather than an empty list,
 * and appends the record's own value when the clinic no longer offers it, so
 * the record in front of the clinician can still be saved.
 *
 * @param {Array<{label: string}>} rows configured rows, possibly empty
 * @param {string} currentValue what this record holds
 * @param {string} listKey
 * @returns {string[]}
 */
export const offeredStatusLabels = (rows, currentValue, listKey) => {
  const configured = (rows || []).map((row) => row.label).filter(Boolean);
  const offered = configured.length ? configured : fallbackLabelsFor(listKey);

  const current = String(currentValue ?? '').trim();
  if (!current) return offered;
  return offered.some((label) => norm(label) === norm(current))
    ? offered
    : [...offered, current];
};

// Exported for the test that compares them against the server's definitions.
export const __FALLBACK_MEANINGS = FALLBACK_MEANINGS;
export const __RESOLVED_MEANINGS = RESOLVED_MEANINGS;
export const __MEANING_RULES = MEANING_RULES;
